import {memo, useCallback, useEffect, useMemo, useState} from 'react';

import {Box, Typography} from '@mui/material';
import {useTranslation} from 'react-i18next';

import {AppButton, AppButtonVariant} from '@/app/shared-components';

import {IGroupMember} from '../../models/interfaces';

export interface GroupSelectPayerContentProps {
  members: IGroupMember[];
  selectedPayerId: string;
  currentUserId?: string;
  expenseTitle: string;
  onConfirm: (payerId: string) => void;
  onClose: () => void;
}

type PayerRowProps = {
  member: IGroupMember;
  selected: boolean;
  onSelect: (memberId: string) => void;
};

const PayerRow = memo(function PayerRow({
  member,
  selected,
  onSelect,
}: PayerRowProps) {
  const {t} = useTranslation();

  const handleClick = useCallback(
    () => onSelect(member.id),
    [member.id, onSelect],
  );

  const memberName = useMemo(() => {
    if (member.isYou) {
      return `${member.name} (${t('groups.add_expense_form.you')})`;
    }
    return member.name;
  }, [member.isYou, member.name, t]);

  return (
    <Box
      id={`GroupSelectPayer_row_${member.id}`}
      role="radio"
      tabIndex={0}
      aria-checked={selected}
      onClick={handleClick}
      className="group-select-payer-row">
      <Box className={`group-select-payer-radio${selected ? ' selected' : ''}`}>
        {selected && <Box className="group-select-payer-radio-dot" />}
      </Box>

      <Typography className="group-select-payer-row-name">
        {memberName}
      </Typography>
    </Box>
  );
});

const GroupSelectPayerContent: React.FC<GroupSelectPayerContentProps> = ({
  members,
  selectedPayerId,
  currentUserId,
  expenseTitle,
  onConfirm,
  onClose,
}) => {
  const [pendingPayerId, setPendingPayerId] = useState(selectedPayerId);

  // Reset the local pending value whenever the parent's selection changes,
  // e.g. when the modal is re-opened with a different current selection.
  useEffect(() => {
    setPendingPayerId(selectedPayerId);
  }, [selectedPayerId]);

  const handleSelect = useCallback((memberId: string) => {
    setPendingPayerId(memberId);
  }, []);

  const handleConfirm = useCallback(() => {
    if (!pendingPayerId) {
      onClose();
      return;
    }

    onConfirm(pendingPayerId);
  }, [onClose, onConfirm, pendingPayerId]);

  const headingText = useMemo(() => {
    const trimmedTitle = expenseTitle.trim();

    if (!trimmedTitle) return 'Who paid?';

    return `Who paid for ${trimmedTitle}?`;
  }, [expenseTitle]);

  const confirmLabel = useMemo(() => {
    if (!pendingPayerId) return 'Confirm';

    if (currentUserId && pendingPayerId === currentUserId) return 'I paid';

    const member = members.find(m => m.id === pendingPayerId);

    return member ? `${member.name} paid` : 'Confirm';
  }, [pendingPayerId, currentUserId, members]);

  return (
    <Box className="group-select-payer-modal">
      <Box className="group-select-payer-header">
        <Typography
          id="GroupSelectPayer_title"
          className="group-select-payer-title">
          {headingText}
        </Typography>
      </Box>

      <Box
        className="group-select-payer-list"
        role="radiogroup"
        aria-labelledby="GroupSelectPayer_title">
        {members.map(member => (
          <PayerRow
            key={member.id}
            member={member}
            selected={member.id === pendingPayerId}
            onSelect={handleSelect}
          />
        ))}
      </Box>

      <Box className="group-select-payer-actions">
        <AppButton
          id="GroupSelectPayer_confirmButton"
          label={confirmLabel}
          onClick={handleConfirm}
          variant={AppButtonVariant.onPrimary}
          disabled={!pendingPayerId}
        />
      </Box>
    </Box>
  );
};

export default GroupSelectPayerContent;
